import axios from "axios";
import { isInactiveTooLong, clearActivity, touchActivity } from "utils/activity";

const terminalHttpClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

terminalHttpClient.interceptors.request.use((config) => {
  touchActivity();

  const url = config?.url || "";
  const isRefresh = url.includes("/api/terminal-auth/refresh");
  if (isRefresh) return config;

  const saved = localStorage.getItem("auth");
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      const token = parsed?.token;
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // ignore
    }
  }

  return config;
});

let isRefreshing = false;
let queue = [];

function subscribe(cb) {
  queue.push(cb);
}

function notifyAll(token) {
  queue.forEach((cb) => cb(token));
  queue = [];
}

terminalHttpClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Network / CORS / etc
    if (!err.response) return Promise.reject(err);

    const original = err.config;
    const status = err.response.status;
    const url = original?.url || "";

    const isAuthEndpoint =
      url.includes("/api/terminal-auth/login") ||
      url.includes("/api/terminal-auth/refresh") ||
      url.includes("/api/terminal-auth/logout");

    // ✅ IMPORTANT:
    // - Refresh ONLY on 401 (expired/invalid access token)
    // - Do NOT refresh on 403 (permission denied)
    const shouldTryRefresh = status === 401;

    if (shouldTryRefresh && !original._retry && !isAuthEndpoint) {
      original._retry = true;

      // if inactive too long, do NOT refresh; log out immediately
      if (isInactiveTooLong()) {
        clearActivity();
        localStorage.removeItem("terminalAuth");
        window.location.href = "/terminal/authentication/sign-in";
        return Promise.reject(err);
      }

      // wait if a refresh is already in progress
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribe((newToken) => {
            if (!newToken) return reject(err);

            original.headers = original.headers ?? {};
            original.headers.Authorization = `Bearer ${newToken}`;
            resolve(httpClient(original));
          });
        });
      }

      isRefreshing = true;

      try {
        // refresh cookie sent automatically because withCredentials=true
        const refreshRes = await httpClient.post("/api/terminal-auth/refresh", null, {
          withCredentials: true,
        });

        const newToken = refreshRes.data?.token;
        if (!newToken) throw new Error("No token returned from refresh");

        // update auth in localStorage
        const saved = localStorage.getItem("auth");
        const parsed = saved ? JSON.parse(saved) : {};
        localStorage.setItem("auth", JSON.stringify({ ...parsed, token: newToken }));

        notifyAll(newToken);

        // retry original request once with new token
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return httpClient(original);
      } catch (refreshErr) {
        console.trace("Redirecting to login because refresh failed", refreshErr);

        notifyAll(null);
        clearActivity();
        localStorage.removeItem("auth");

        // ✅ If you want immediate redirect on refresh failure:
        window.location.href = "/terminal/authentication/sign-in";

        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    // ✅ If it's 403, just pass it up; UI should show "Forbidden" NOT logout
    return Promise.reject(err);
  }
);

export default terminalHttpClient;
